import React, { useState, useEffect, useMemo } from 'react';
import { teacherApi } from '../../services/api';
import VideoWithSpeed from '../../components/VideoWithSpeed';

// Teacher Digikoç — same shape as the student page (same admin-uploaded
// content_items source), but hits /teacher/digikoc so the API surface stays
// split per role per spec.

interface DigikocItem {
  id: string;
  kind: 'podcast' | 'sinava_dogru';
  title: string;
  thumbnail_url: string | null;
  duration: string | null;
  video_url: string | null;
  created_at: string;
  /** İçerik Yükle'de opsiyonel Ders seçilirse dolu — "Tüm Dersler" filtresini besler. */
  subject_id?: number | null;
  subject_name?: string | null;
}

const TeacherDigikoc: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'podcast' | 'video'>('podcast');
  const [podcasts, setPodcasts] = useState<DigikocItem[]>([]);
  const [videos, setVideos] = useState<DigikocItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<DigikocItem | null>(null);

  useEffect(() => {
    setLoading(true);
    teacherApi.digikoc()
      .then((r) => {
        setPodcasts(r.data.podcasts ?? []);
        setVideos(r.data.videos ?? []);
      })
      .catch((e: any) => setError(e?.response?.data?.error ?? e.message))
      .finally(() => setLoading(false));
  }, []);

  const queue = useMemo(() =>
    [...podcasts, ...videos]
      .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at))
      .slice(0, 6),
    [podcasts, videos]);

  // "Tüm Dersler" — gerçek filtre (öğrenci sayfasıyla aynı): ders atanmış
  // videolardan seçenek listesi kurulur, seçim video kolonunu daraltır.
  const [dersFilter, setDersFilter] = useState<string>('');
  const dersOptions = useMemo(
    () => Array.from(new Set(videos.map((v) => v.subject_name).filter(Boolean))) as string[],
    [videos],
  );
  const filteredVideos = dersFilter ? videos.filter((v) => v.subject_name === dersFilter) : videos;
  // Aynı filtre podcast kolonu için de (bağımsız seçim).
  const [podFilter, setPodFilter] = useState<string>('');
  const podOptions = useMemo(
    () => Array.from(new Set(podcasts.map((p) => p.subject_name).filter(Boolean))) as string[],
    [podcasts],
  );
  const filteredPodcasts = podFilter ? podcasts.filter((p) => p.subject_name === podFilter) : podcasts;

  const renderItemCard = (it: DigikocItem, accent: string) => (
    <button
      key={it.id}
      onClick={() => it.video_url && setPlaying(it)}
      className={`bg-white rounded-[14px] p-3 xl:p-4 border border-gray-100 cursor-pointer hover:border-[${accent}] transition-colors w-full text-left`}
    >
      <div className="flex items-center gap-3">
        {it.thumbnail_url && (
          <img src={it.thumbnail_url} alt="" className="w-12 h-12 rounded-[8px] object-cover flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[12px] xl:text-[16px] font-bold text-[#2b2b2b] truncate">{it.title}</p>
          {it.duration && <p className="text-[11px] text-gray-500 mt-0.5">{it.duration}</p>}
        </div>
      </div>
    </button>
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h1 className="text-[18px] xl:text-[24px] font-semibold text-[#2b2b2b]">Digikoç</h1>
        <svg width="18" height="20" viewBox="0 0 24 28" fill="none" stroke="#2b2b2b" strokeWidth="1.5">
          <path d="M18 10a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 23a2 2 0 01-3.46 0"/>
        </svg>
      </div>

      <div className="flex gap-3 mb-3" style={{ aspectRatio: '1493/381' }}>
        <div className="w-[56.5%] rounded-[16px] overflow-hidden">
          <img src="/a-dk-banner-full.png" alt="Digikoç" className="w-full h-full object-cover" />
        </div>
        <div className="w-[43.5%] rounded-[16px] overflow-hidden">
          {/* Statik player görseli yerine İzleme/Dinleme Sırası'nın ilk öğesi
              (öğrenci sayfasıyla aynı) — kapak img'i absolute: doğal boyutu
              satırı şişirmesin. */}
          {queue.length > 0 && queue[0].video_url ? (
            <button
              onClick={() => setPlaying(queue[0])}
              title={queue[0].title}
              className="relative block w-full h-full cursor-pointer group text-left overflow-hidden"
            >
              <img
                src={queue[0].thumbnail_url || '/a-dk-video-full.png'}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4">
                <div className="w-11 h-11 xl:w-14 xl:h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  <svg width="16" height="18" viewBox="0 0 10 12" fill="#3973bf" className="ml-0.5"><path d="M0 0l10 6-10 6z"/></svg>
                </div>
                <p className="max-w-full truncate text-center text-white text-[12px] xl:text-[15px] font-semibold drop-shadow">
                  {queue[0].title}
                </p>
              </div>
            </button>
          ) : (
            <img src="/a-dk-video-full.png" alt="Video" className="w-full h-full object-cover" />
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <div style={{ width: '66.6%' }} className="min-w-0">
          <div className="flex rounded-t-[16px] overflow-hidden">
            <button onClick={() => setActiveTab('podcast')} className="w-1/2 cursor-pointer hover:brightness-110 transition-all">
              <img src="/a-dk-ptab.png" alt="Digikoç Podcastler" className="w-full h-auto" />
            </button>
            <button onClick={() => setActiveTab('video')} className="w-1/2 cursor-pointer hover:brightness-110 transition-all">
              <img src="/a-dk-vtab.png" alt="Sınava Doğru Video Serisi" className="w-full h-auto" />
            </button>
          </div>

          <div className="bg-[#fafafa] rounded-b-[16px] border border-gray-100 border-t-0 min-h-[280px] xl:min-h-[450px]">
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <p className="text-center text-red-400 py-8">{error}</p>
            ) : (
              <div className="flex divide-x divide-gray-200">
                <div className="flex-1 p-3 xl:p-4">
                  <select
                    value={podFilter}
                    onChange={(e) => setPodFilter(e.target.value)}
                    className="w-full bg-white rounded-[10px] p-2 xl:p-3 mb-2 xl:mb-3 border border-gray-100 text-[12px] xl:text-[16px] font-bold text-[#2b2b2b] cursor-pointer"
                  >
                    <option value="">Tüm Dersler</option>
                    {podOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <div className="space-y-2 xl:space-y-3">
                    {podcasts.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-8">Henüz podcast yüklenmedi.</p>
                    ) : filteredPodcasts.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-8">Bu derste podcast yok.</p>
                    ) : (
                      filteredPodcasts.map((t) => renderItemCard(t, '#e5599b'))
                    )}
                  </div>
                </div>
                <div className="flex-1 p-3 xl:p-4">
                  <select
                    value={dersFilter}
                    onChange={(e) => setDersFilter(e.target.value)}
                    className="w-full bg-white rounded-[10px] p-2 xl:p-3 mb-2 xl:mb-3 border border-gray-100 text-[12px] xl:text-[16px] font-bold text-[#2b2b2b] cursor-pointer"
                  >
                    <option value="">Tüm Dersler</option>
                    {dersOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <div className="space-y-2 xl:space-y-3">
                    {videos.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-8">Henüz video yüklenmedi.</p>
                    ) : filteredVideos.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-8">Bu derste video yok.</p>
                    ) : (
                      filteredVideos.map((v) => renderItemCard(v, '#3973bf'))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ width: '33.4%' }} className="flex-shrink-0 hidden lg:block">
          <div className="rounded-[16px] border border-gray-100 overflow-hidden h-full">
            <div className="overflow-hidden rounded-t-[16px]">
              <img src="/a-dk-qhdr.png" alt="" className="w-full h-auto" />
            </div>
            <div className="bg-[#fafafa] p-3 xl:p-4 space-y-2 xl:space-y-3 min-h-[200px]">
              {loading ? (
                <p className="text-gray-400 text-sm text-center py-8">Yükleniyor…</p>
              ) : queue.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">Yakında eklenecek.</p>
              ) : (
                queue.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => q.video_url && setPlaying(q)}
                    className="bg-white rounded-[10px] p-2.5 xl:p-3 flex items-center gap-2 border border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors w-full text-left"
                  >
                    <div className="w-[24px] h-[24px] xl:w-[34px] xl:h-[34px] rounded-full bg-[#3973bf] flex items-center justify-center flex-shrink-0">
                      <svg width="8" height="10" viewBox="0 0 10 12" fill="white"><path d="M0 0l10 6-10 6z"/></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] xl:text-[14px] font-semibold text-[#2b2b2b] truncate">{q.title}</p>
                      {q.duration && <p className="text-[8px] xl:text-[12px] text-gray-500">{q.duration}</p>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {playing && playing.video_url && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4" onClick={() => setPlaying(null)}>
          <div className="relative w-full max-w-[900px]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPlaying(null)} className="absolute -top-10 right-0 text-white text-xl">✕ Kapat</button>
            <VideoWithSpeed autoPlay src={playing.video_url} className="w-full rounded-[12px] bg-black" />
            <p className="text-white text-center mt-3">{playing.title}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDigikoc;
